import { Pencil, RotateCcw, Search, Trash2, X } from 'lucide-react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { positionService } from '../api/services/position.service'
import { PermissionGate } from '../components/auth/PermissionGate'
import { AppShell } from '../components/layout/AppShell'
import { Button } from '../components/ui/button'
import { FieldLabel, FormRequiredLegend } from '../components/ui/field-label'
import { Input } from '../components/ui/input'
import { ListPagination } from '../components/ui/list-pagination'
import { SelectPicker, type SelectOption } from '../components/ui/select-picker'
import { SoftDeleteConfirmDialog } from '../components/ui/soft-delete-confirm-dialog'
import { DEFAULT_PAGE_SIZE, resolveListPage, type PaginationMeta } from '../lib/api-list'
import { IconActionButton, TableIconActions } from '../components/ui/table-icon-actions'
import { useAuth } from '../context/AuthContext'
import type { Position, PositionFormPayload } from '../types/position'

type ActiveFilter = 'all' | 'active' | 'disabled'
const ACTIVE_OPTIONS: SelectOption[] = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Inactive' },
]

const positionSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  is_active: z.boolean(),
})

type PositionFormValues = z.infer<typeof positionSchema>

const emptyForm: PositionFormValues = {
  name: '',
  is_active: true,
}

export function PositionsPage() {
  const { can } = useAuth()
  const [positions, setPositions] = useState<Position[]>([])
  const [hasLoaded, setHasLoaded] = useState(false)
  const [withTrashed, setWithTrashed] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState<ActiveFilter>('all')
  const [formOpen, setFormOpen] = useState(false)
  const [editing, setEditing] = useState<Position | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<Position | null>(null)
  const [deleting, setDeleting] = useState(false)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState<number>(DEFAULT_PAGE_SIZE)
  const [meta, setMeta] = useState<PaginationMeta | null>(null)

  const form = useForm<PositionFormValues>({
    resolver: zodResolver(positionSchema),
    defaultValues: emptyForm,
  })

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (search.trim()) count += 1
    if (activeFilter !== 'all') count += 1
    if (withTrashed) count += 1
    return count
  }, [activeFilter, search, withTrashed])

  const clearFilters = () => {
    setSearch('')
    setSearchInput('')
    setActiveFilter('all')
    setWithTrashed(false)
  }

  const loadPositions = useCallback(async () => {
    try {
      const response = await positionService.list({
        page,
        per_page: perPage,
        only_trashed: withTrashed || undefined,
        search: search.trim() || undefined,
        is_active: activeFilter === 'all' ? undefined : activeFilter === 'active',
      })
      const { items, meta: nextMeta } = resolveListPage(response, page, perPage)
      setPositions(items)
      setMeta(nextMeta)
    } catch {
      setPositions([])
      setMeta(null)
    } finally {
      setHasLoaded(true)
    }
  }, [activeFilter, page, perPage, search, withTrashed])

  useEffect(() => {
    void loadPositions()
  }, [loadPositions])

  useEffect(() => {
    setPage(1)
  }, [activeFilter, search, withTrashed])

  const openCreate = () => {
    setEditing(null)
    form.reset(emptyForm)
    setFormOpen(true)
  }

  const openEdit = (position: Position) => {
    setEditing(position)
    form.reset({
      name: position.name,
      is_active: position.is_active,
    })
    setFormOpen(true)
  }

  const closeForm = () => {
    setFormOpen(false)
    setEditing(null)
    form.reset(emptyForm)
  }

  const onSubmit = async (values: PositionFormValues) => {
    setSubmitting(true)

    const payload: PositionFormPayload = {
      name: values.name,
      is_active: values.is_active,
    }

    try {
      if (editing) {
        await positionService.update(editing.id, payload)
      } else {
        await positionService.create(payload)
      }

      closeForm()
      await loadPositions()
    } catch {
      // handled globally
    } finally {
      setSubmitting(false)
    }
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) {
      return
    }

    setDeleting(true)

    try {
      await positionService.remove(deleteTarget.id)
      setDeleteTarget(null)
      await loadPositions()
    } catch {
      // handled globally
    } finally {
      setDeleting(false)
    }
  }

  const handleRestore = async (position: Position) => {
    try {
      await positionService.restore(position.id)
      await loadPositions()
    } catch {
      // handled globally
    }
  }

  return (
    <AppShell>
      <div className="mx-auto max-w-6xl px-2 py-2">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="font-display text-2xl font-bold">Positions</h1>
            <p className="mt-1 text-sm text-ink-600">
              Manage clinical position reference data for users of any role
              (required for doctor signup; optional for assistants and reviewers).
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-ink-700">
              <input
                type="checkbox"
                className="h-4 w-4 accent-green-600"
                checked={withTrashed}
                onChange={(event) => setWithTrashed(event.target.checked)}
              />
              Show Deleted
            </label>

            <PermissionGate permission="positions-create">
              <Button onClick={openCreate}>Add Position</Button>
            </PermissionGate>
          </div>
        </div>

        <section className="mb-6 rounded-xl border border-parch-100 bg-white p-4 shadow-card sm:p-5">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-ink-900">Filters</h2>
              {activeFilterCount > 0 && (
                <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-green-100 px-1.5 text-xs font-semibold text-green-700">
                  {activeFilterCount}
                </span>
              )}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-end gap-2">
            <div className="min-w-[12rem] flex-1 basis-0">
              <FieldLabel>Name</FieldLabel>
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
                <Input
                  className="pl-9"
                  placeholder="Search position name…"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      setSearch(searchInput.trim())
                    }
                  }}
                />
              </div>
            </div>
            <div className="min-w-[12rem] flex-1 basis-0">
              <FieldLabel>Status</FieldLabel>
              <SelectPicker
                options={ACTIVE_OPTIONS}
                value={activeFilter}
                onChange={(value) => setActiveFilter(value as ActiveFilter)}
                placeholder="All"
              />
            </div>
            <Button onClick={() => setSearch(searchInput.trim())}>Search</Button>
            {activeFilterCount > 0 && (
              <Button variant="secondary" onClick={clearFilters}>
                <X className="h-4 w-4" />
                Clear
              </Button>
            )}
          </div>
        </section>

        <section className="overflow-hidden rounded-lg border border-parch-100 bg-white shadow-card">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead className="border-b border-parch-100 bg-parch-50 text-ink-600">
                <tr>
                  <th className="px-4 py-3 font-medium">Name</th>
                  <th className="px-4 py-3 font-medium">Active</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {hasLoaded && positions.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-6 text-sm text-ink-500">
                      No positions found.
                    </td>
                  </tr>
                ) : (
                  positions.map((position) => (
                    <tr key={position.id} className="border-b border-parch-50 last:border-0">
                      <td className="px-4 py-3">{position.name}</td>
                      <td className="px-4 py-3">{position.is_active ? 'Yes' : 'No'}</td>
                      <td className="px-4 py-3">
                        {position.deleted_at ? (
                          <span className="text-danger">Deleted</span>
                        ) : (
                          <span className="text-green-700">Active</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <TableIconActions>
                          {can('positions-update') && !position.deleted_at && (
                            <IconActionButton
                              icon={Pencil}
                              label="Edit"
                              onClick={() => openEdit(position)}
                            />
                          )}
                          {can('positions-delete') && !position.deleted_at && (
                            <IconActionButton
                              icon={Trash2}
                              label="Delete"
                              variant="destructive"
                              onClick={() => setDeleteTarget(position)}
                            />
                          )}
                          {can('positions-restore') && position.deleted_at && (
                            <IconActionButton
                              icon={RotateCcw}
                              label="Restore"
                              onClick={() => void handleRestore(position)}
                            />
                          )}
                        </TableIconActions>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <ListPagination
          meta={meta}
          perPage={perPage}
          onPageChange={setPage}
          onPerPageChange={(n) => {
            setPerPage(n)
            setPage(1)
          }}
        />

        <SoftDeleteConfirmDialog
          open={deleteTarget !== null}
          entityLabel="position"
          itemName={deleteTarget?.name ?? ''}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => void handleDeleteConfirm()}
          confirming={deleting}
        />

        {formOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-card">
              <h2 className="font-display text-xl font-bold">
                {editing ? 'Edit Position' : 'Create Position'}
              </h2>

              <form className="mt-6 space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
                <FormRequiredLegend />
                <div>
                  <FieldLabel required>Name</FieldLabel>
                  <Input {...form.register('name')} />
                  {form.formState.errors.name && (
                    <p className="mt-1 text-xs text-danger">{form.formState.errors.name.message}</p>
                  )}
                </div>

                <label className="flex items-center gap-2 text-sm">
                  <input type="checkbox" {...form.register('is_active')} />
                  Active
                </label>

                <div className="flex justify-end gap-2 pt-2">
                  <Button type="button" variant="secondary" onClick={closeForm}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={submitting}>
                    {submitting ? 'Saving…' : editing ? 'Save Changes' : 'Create Position'}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  )
}
